import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  increment,
  type DocumentSnapshot,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../lib/firebase/app';
import type { BoardPost, BoardComment } from '../types/board';

const PAGE_SIZE = 10;

function boardCollection(lang: string) {
  return `board-${lang}`;
}

/** Fetch a page of posts. */
export async function fetchPosts(
  lang: string,
  lastDoc?: DocumentSnapshot,
  pageSize: number = PAGE_SIZE,
): Promise<{ posts: BoardPost[]; lastDoc: DocumentSnapshot | undefined }> {
  const q = lastDoc
    ? query(collection(db, boardCollection(lang)), orderBy('createdAt', 'desc'), startAfter(lastDoc), limit(pageSize))
    : query(collection(db, boardCollection(lang)), orderBy('createdAt', 'desc'), limit(pageSize));

  const snap = await getDocs(q);

  const posts = snap.docs.map((d) => ({ id: d.id, ...d.data() } as BoardPost));
  const last = snap.docs[snap.docs.length - 1];

  return { posts, lastDoc: last };
}

/** Get a single post. */
export async function getPost(lang: string, postId: string): Promise<BoardPost | null> {
  const snap = await getDoc(doc(db, boardCollection(lang), postId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() } as BoardPost;
}

/** Create a post. */
export async function createPost(lang: string, data: {
  title: string;
  content: string;
  prefix: string;
  authorId: string;
  authorNickname: string;
  authorProfileImageUrl?: string;
  authorGender?: string;
  isNotice?: boolean;
  isReleaseNote?: boolean;
  isInfoIconLinked?: boolean;
}): Promise<string> {
  const ref = await addDoc(collection(db, boardCollection(lang)), {
    ...data,
    isNotice: data.isNotice ?? false,
    isAdminOnly: false,
    isReleaseNote: data.isReleaseNote ?? false,
    isInfoIconLinked: data.isInfoIconLinked ?? false,
    likeCount: 0,
    commentCount: 0,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  return ref.id;
}

/** Delete a post. */
export async function deletePost(lang: string, postId: string): Promise<void> {
  await deleteDoc(doc(db, boardCollection(lang), postId));
}

/** Fetch comments for a post. */
export async function fetchComments(lang: string, postId: string): Promise<BoardComment[]> {
  const q = query(
    collection(db, boardCollection(lang), postId, 'boardComments'),
    orderBy('createdAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() } as BoardComment));
}

/** Add a comment to a post. */
export async function addComment(lang: string, postId: string, data: {
  content: string;
  authorId: string;
  authorNickname: string;
}): Promise<string> {
  const ref = await addDoc(
    collection(db, boardCollection(lang), postId, 'boardComments'),
    {
      ...data,
      createdAt: serverTimestamp(),
    },
  );
  // Increment comment count
  await updateDoc(doc(db, boardCollection(lang), postId), {
    commentCount: increment(1),
  });
  return ref.id;
}

/** Get the info-icon linked post. */
export async function getInfoIconPost(lang: string): Promise<BoardPost | null> {
  const q = query(collection(db, boardCollection(lang)), where('isInfoIconLinked', '==', true), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() } as BoardPost;
}

/** Get latest release note post. */
export async function getLatestReleaseNote(lang: string): Promise<BoardPost | null> {
  const q = query(collection(db, boardCollection(lang)), where('isReleaseNote', '==', true), orderBy('createdAt', 'desc'), limit(1));
  const snap = await getDocs(q);
  return snap.empty ? null : { id: snap.docs[0].id, ...snap.docs[0].data() } as BoardPost;
}

/** Get latest notice posts. */
export async function getNotices(lang: string): Promise<BoardPost[]> {
  const q = query(collection(db, boardCollection(lang)), where('isNotice', '==', true), orderBy('createdAt', 'desc'), limit(5));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as BoardPost));
}

/** Load prefix options from Firestore boardSettings. */
export async function loadPrefixOptions(lang: string): Promise<{ key: string; label: string }[]> {
  const defaults = [
    { key: 'none', label: lang === 'ko' ? '말머리 없음' : 'None' },
    { key: 'question', label: lang === 'ko' ? '질문' : 'Question' },
    { key: 'frequencyShare', label: lang === 'ko' ? '주파수 공유' : 'Frequency Share' },
    { key: 'review', label: lang === 'ko' ? '후기' : 'Review' },
  ];
  try {
    const snap = await getDoc(doc(db, 'boardSettings', `${boardCollection(lang)}-prefixes`));
    if (snap.exists() && snap.data().options) {
      return snap.data().options;
    }
    // Create defaults if not exists
    const { setDoc: sd } = await import('firebase/firestore');
    await sd(doc(db, 'boardSettings', `${boardCollection(lang)}-prefixes`), { options: defaults }).catch(() => {});
    return defaults;
  } catch { return defaults; }
}

/** Increment view count. */
export async function incrementViewCount(lang: string, postId: string): Promise<void> {
  await updateDoc(doc(db, boardCollection(lang), postId), { views: increment(1) });
}

/** Delete a comment. */
export async function deleteComment(lang: string, postId: string, commentId: string): Promise<void> {
  await deleteDoc(doc(db, boardCollection(lang), postId, 'boardComments', commentId));
  // Decrement comment count
  await updateDoc(doc(db, boardCollection(lang), postId), {
    commentCount: increment(-1),
  });
}

/** Update a post. */
export async function updatePost(lang: string, postId: string, data: {
  title: string;
  content: string;
  prefix: string;
}): Promise<void> {
  await updateDoc(doc(db, boardCollection(lang), postId), {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/** Search posts by title or content. */
export async function searchPosts(
  lang: string,
  keyword: string,
  searchType: 'title' | 'content' | 'author' = 'title',
): Promise<BoardPost[]> {
  // Firestore doesn't support full-text search natively,
  // so we fetch recent posts and filter client-side.
  // For production, consider Algolia or Typesense.
  const q = query(
    collection(db, boardCollection(lang)),
    orderBy('createdAt', 'desc'),
    limit(100),
  );
  const snap = await getDocs(q);
  const kw = keyword.toLowerCase();
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as BoardPost))
    .filter((p) => {
      switch (searchType) {
        case 'title': return p.title.toLowerCase().includes(kw);
        case 'content': return p.content.toLowerCase().includes(kw);
        case 'author': return p.authorNickname.toLowerCase().includes(kw);
        default: return p.title.toLowerCase().includes(kw) || p.content.toLowerCase().includes(kw);
      }
    });
}
